/**
 * SkillRadarChart Component
 *
 * Recharts RadarChart showing compliance / efficiency / safety scores.
 * Renders current month as a filled Guild Green polygon and optional
 * previous month as a dashed gray outline.
 *
 * Hidden entirely when all current values are 0 (caller checks this).
 */

import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';

interface SkillRadarChartProps {
  current: { compliance: number; efficiency: number; safety: number };
  previous: { compliance: number; efficiency: number; safety: number } | null;
}

/** Radar data point shape for Recharts */
interface RadarDataPoint {
  subject: string;
  current: number;
  previous: number | undefined;
}

export function SkillRadarChart({ current, previous }: SkillRadarChartProps) {
  const data: RadarDataPoint[] = [
    {
      subject: 'Compliance',
      current: current.compliance,
      previous: previous?.compliance,
    },
    {
      subject: 'Efficiency',
      current: current.efficiency,
      previous: previous?.efficiency,
    },
    {
      subject: 'Safety',
      current: current.safety,
      previous: previous?.safety,
    },
  ];

  return (
    <div>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
          <PolarGrid gridType="polygon" stroke="#e5e7eb" />

          <PolarAngleAxis
            dataKey="subject"
            tick={{ fontSize: 13, fill: '#4b5563', fontWeight: 500 }}
          />

          <PolarRadiusAxis
            domain={[0, 100]}
            tick={false}
            axisLine={false}
            tickCount={5}
          />

          {/* Previous month -- dashed outline, no fill (only if data exists) */}
          {previous !== null && (
            <Radar
              name="Previous"
              dataKey="previous"
              stroke="#9ca3af"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="transparent"
              fillOpacity={0}
            />
          )}

          {/* Current month -- filled Guild Green */}
          <Radar
            name="Current"
            dataKey="current"
            stroke="#00674a"
            strokeWidth={2}
            fill="#00674a"
            fillOpacity={0.3}
          />
        </RadarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 -mt-2">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#00674a' }} />
          <span className="text-xs font-medium text-op-text-secondary">This Month</span>
        </div>
        {previous !== null && (
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#9ca3af' }} />
            <span className="text-xs font-medium text-op-text-tertiary">Last Month</span>
          </div>
        )}
      </div>
    </div>
  );
}
